import React from 'react'
import { Link } from '@inertiajs/react'
import PortfolioLayout from '@/layouts/PortfolioLayout'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Megaphone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { announcementIcon, formatAnnouncementDate } from '@/lib/announcements'
import type { AnnouncementData, AnnouncementKind, AccountPropertyGroup, CurrentUser } from '@/types/shared'

// "WHAT'S NEW" IS THE CHANGELOG (docs/plans/2026-08-14-product-announcements.md, decision 3).
// Every kind lands here, newest first; a `feature` also became a floating card and a `milestone`
// also became the Spotlight (§13), but the feed is where all three end up. There is no separate
// changelog modal and there never will be.
//
// Global surface, like the guides: an announcement describes the PRODUCT, not a hotel — so this
// page has no property in scope, and `tryItHref` is null on every row by design. "Learn more"
// resolves through the guides, which work from anywhere, and is the only link a row carries here.
//
// No covers on this page, deliberately: the sandboxed art is the CARD's moment. A feed of a dozen
// looping iframes is a dozen animations competing on one screen, and it would push the thing an
// operator came here to read — what changed — below the fold.

interface WhatsNewProps {
  currentUser: CurrentUser
  properties?: AccountPropertyGroup[]
  announcements: AnnouncementData[]
}

// Hotelier plain speech, not release-engineering nouns. `feature` is the only kind that gets the
// brand colour — it is the only kind that changes what the operator can do. `milestone` gets the
// warm amber instead: it marks a moment rather than a control, so it should read as different from
// "here is a new switch", not as a louder version of it.
const KIND_META: Record<AnnouncementKind, { label: string; className: string }> = {
  feature: { label: 'New', className: 'bg-primary/10 text-primary' },
  improvement: { label: 'Improved', className: 'bg-slate-100 text-slate-700' },
  fix: { label: 'Fixed', className: 'bg-slate-100 text-slate-700' },
  milestone: { label: 'Milestone', className: 'bg-amber-100 text-amber-700' },
}

function KindChip({ kind }: { kind: AnnouncementKind }) {
  const meta = KIND_META[kind] ?? KIND_META.improvement
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', meta.className)}>
      {meta.label}
    </span>
  )
}

function FeedRow({ announcement, index }: { announcement: AnnouncementData; index: number }) {
  const items = announcement.body.items ?? []
  const paragraph = announcement.body.paragraph
  const published = formatAnnouncementDate(announcement.publishedAt)

  return (
    <article
      className="animate-slide-in rounded-xl border border-border/60 bg-white p-5 shadow-sm"
      style={{ animationDelay: `${60 + index * 50}ms` }}
    >
      <div className="flex items-center gap-2">
        <KindChip kind={announcement.kind} />
        {announcement.tagLabel && (
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {announcement.tagLabel}
          </span>
        )}
        {published && (
          <span className="ml-auto text-xs tabular-nums text-muted-foreground">{published}</span>
        )}
      </div>

      <h2 className="mt-2.5 text-sm font-semibold tracking-tight text-foreground">{announcement.title}</h2>

      {items.length > 0 && (
        <ul className="mt-3 space-y-2.5">
          {items.map((item, i) => {
            const Icon = announcementIcon(item.icon)
            return (
              <li key={`${item.heading}-${i}`} className="flex gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <p className="min-w-0 text-sm leading-relaxed text-muted-foreground">
                  <span className="font-medium text-foreground">{item.heading}</span>
                  {' — '}
                  {item.text}
                </p>
              </li>
            )
          })}
        </ul>
      )}

      {items.length === 0 && paragraph && (
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{paragraph}</p>
      )}

      {announcement.learnMoreHref && (
        <Link
          href={announcement.learnMoreHref}
          className="mt-3 inline-block text-xs font-medium text-primary hover:underline"
        >
          Learn more
        </Link>
      )}
    </article>
  )
}

export default function WhatsNew({ currentUser, properties, announcements }: WhatsNewProps) {
  const rows = announcements ?? []

  return (
    <PortfolioLayout title="What's new" currentUser={currentUser} properties={properties}>
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">What's new</h1>
            <HelpTooltip content="Everything we have shipped recently — new controls, improvements and fixes. The newest is at the top." />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            What changed in the last few releases, and where to find it.
          </p>
        </div>

        {rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
            <Megaphone className="mx-auto h-8 w-8 text-muted-foreground/40" />
            <p className="mt-3 text-sm font-medium text-foreground">Nothing new just yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              When we ship something that changes how your rooms are priced, you will read about it here first.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((announcement, index) => (
              <FeedRow key={announcement.id} announcement={announcement} index={index} />
            ))}
          </div>
        )}
      </div>
    </PortfolioLayout>
  )
}
